<template>
    <ui-container class="py-8">
        <ui-header
            :title="editMode ? 'Editing Supplier Quote': 'Create new Supplier Quote'"
            :subtitle="editMode ? 'ID #'+this.item.id : null">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="finished()" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <div class="grid gap-6 grid-cols-12 py-6">
            <ui-select-searchable
                class="col-span-12"
                label="Supplier"
                v-model="form.supplier"
                :error="form.errors.get('supplierReference')"
                :create-option="name => ({ value: name, label: name, name: name })"
                :search="searchCompanies"
                :searchDebounce="0"
                @input="save"
            ></ui-select-searchable>
            <ui-date-picker label="Valid From" name="validFrom" v-model="supplierQuote.validFrom" class="xl:col-span-6"
                            placeholder="What is this agreement valid from?"
                            :error="form.errors.get('validFrom')"
                            @input="save"></ui-date-picker>
            <ui-date-picker label="Valid To" name="validTo" v-model="supplierQuote.validTo" class="xl:col-span-6"
                            placeholder="What is this agreement valid to?"
                            :error="form.errors.get('validTo')"
                            @input="save"></ui-date-picker>
        </div>

        <div class="flex border-t border-b -mb-px border-gray-200 bg-gray-50 mt-4 text-gray-500">
            <div class="w-6 flex-shrink-0"></div>
            <div class="flex-1 border-r border-gray-200 px-2 py-1 text-xs font-semibold">Item</div>
            <div class="w-24 -ml-px border-r border-gray-200 px-2 py-1 text-xs font-semibold">Type</div>
            <div class="w-14 -ml-px border-r border-gray-200 px-2 py-1 text-xs font-semibold">UoM</div>
            <div class="w-24 -ml-px border-r border-gray-200 px-2 py-1 text-xs font-semibold">Cost per Unit</div>
            <div class="w-6 ml-2 flex-shrink-0"></div>
        </div>

        <!-- Build Items. -->
        <draggable :list="currentItems" draggable=".draggable" handle=".handle" group="items" @change="reordered"
                   :key="'item'+userKey">
            <supplier-quotes-item
                v-for="(item,index) in currentItems"
                :key="index"
                :item="item"
                :store="store"
                ref="items"
                @removeItem="removeSubItem"
                @updatedItem="save">
            </supplier-quotes-item>
        </draggable>

        <!-- New Material. -->
        <div class="flex border-none mt-px -mb-px">
            <div class="w-6 flex-shrink-0 self-center">
                <i class="fa fa-plus text-xs text-gray-300"></i>
            </div>
            <div class="flex-1 border-none -mr-px">
                <input
                    placeholder="Add item..."
                    class="text-sm -mx-px my-px px-2 py-1.5 border-none shadow-none relative focus:z-10"
                    type="text"
                    v-model="newItemEntry.item"
                    @input="addSubItem"
                >
            </div>
            <div class="w-6 ml-2 flex-shrink-0"></div>
        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new @click="clear" class="ml-3">Clear</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

    </ui-container>

</template>

<script>
import {SupplierQuote} from "@models";
import UiDropdownItem from "../ui/UiDropdownItem.vue";
import UiDropdown from "../ui/UiDropdown.vue";
import RoutePlansJobItem from "../logistics/route-plans/RoutePlansJobItem.vue";
import SupplierQuotesItem from "./SupplierQuotesItem.vue";
import draggable from 'vuedraggable'
import UiButtonNew from "../ui/UiButtonNew.vue";
import moment from "moment";

export default {
    components: {
        UiButtonNew,
        SupplierQuotesItem, draggable, RoutePlansJobItem, UiDropdown, UiDropdownItem
    },
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        store: Object,
        packageReference: Number,
    },
    data() {
        let draft = localStorage.getItem('supplier-quote-draft-' + this.packageReference);
        draft = draft ? JSON.parse(draft) : {items: [],supplierQuote: {}};
        draft = draft ? draft : {items: [],supplierQuote: {}};

        let initialData = this.item ? this.item.getFormAttributes() : draft;
        initialData.supplier = initialData.supplierName;
        return {
            currentItems: initialData.items ? initialData.items : [],
            supplierQuote: initialData.supplierQuote ? initialData.supplierQuote : {},
            newItemEntry: {
                item: null
            },
            userKey: 1,
            form: new Form(initialData, async (form) => {
                let data = form.export();
                data.items = this.currentItems ? this.currentItems : [];

                if (data.supplier instanceof Object) {
                    data.supplierName = data.supplier.name;
                }
                delete data.supplier;
                data.validFrom = this.supplierQuote.validFrom;
                data.validTo = this.supplierQuote.validTo;

                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };
                if (this.editMode) {
                    await this.item.update(data)
                    this.edit(true, sorter)
                } else {
                    const mpa = await SupplierQuote.create(this.packageReference, data)

                    this.addItem(mpa, 0, sorter);
                    this.clear();
                    form.reset()
                }
            }),
        }
    },
    methods: {
        reordered() {
            this.save();
            this.userKey++;
        },
        clear() {
            this.form.supplier = false
            this.supplierQuote.supplier = false
            this.supplierQuote.validFrom = ''
            this.supplierQuote.validTo = ''
            this.currentItems = [];
            localStorage.setItem('supplier-quote-draft-' + this.packageReference, false);
        },
        save() {
            if (this.editMode) {
                if (this.form.supplier instanceof Object) {
                    this.item.supplierName = this.form.supplier.name;
                    this.item.supplierReferece = this.form.supplier.id;
                }
                this.form.submit()
            } else {
                let data = this.form.export();
                if (data.supplier instanceof Object) {
                    data.supplierName = data.supplier.name;
                }
                delete data.supplier;
                data.supplierQuote = this.supplierQuote
                data.items = this.currentItems;
                data.expires = moment().add(5,'day');
                localStorage.setItem('supplier-quote-draft-' + this.packageReference, JSON.stringify(data));
                for (let i = 0; i < localStorage.length; i++){
                   let item = localStorage.getItem(localStorage.key(i));
                   if (item.expires && (item.expires > moment())) {
                       localStorage.removeItem(localStorage.key(i))
                   }
                }
            }
        },
        addSubItem() {
            if (!this.currentItems) {
                this.currentItems = [];
            }
            this.currentItems.push({
                name: this.newItemEntry.item,
                type: 'Material',
                unitOfMeasure: '',
                costPerUnit: '£0.00',
            });
            this.$nextTick(() => {
                this.$refs.items[this.$refs.items.length - 1].$refs.item.focus()
                this.newItemEntry.item = '';
            })
        },
        removeSubItem(item) {
            this.currentItems = this.currentItems.filter((currentItem) => {
                return currentItem !== item
            });
            this.reordered();
        },
        finished() {
            let sorter = (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            };
            this.edit(false, sorter)
        },
        searchCompanies(search) {
            if (this.store.loaded) {
                const query = search.toLowerCase().split(/[^A-Za-z0-9.-]+/);
                return this.store.companies.filter((supplier) => {
                    return query.every((token) => {
                        return (supplier.name).toLowerCase().includes(token);
                    });
                }).sort((a, b) => {
                    let score = 0;
                    return score;
                }).slice(0, 100).map((item) => {
                    return {'label': item.name, 'name': item.name, 'id': item.id};
                })
            }
        },
    },
    async mounted() {
        if (this.item) {
            this.supplierQuote = await SupplierQuote.find(this.item.id)
            this.currentItems = this.supplierQuote.items
        }
    },
    computed: {
        editMode() {
            return !!this.item
        },
    },
}
</script>
